import React, { useMemo } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

export const DEFAULT_SPRINT_LENGTH_DAYS = 14;
export const DEFAULT_SPRINT_START_WEEKDAY = 1; // Monday

export const SPRINT_LENGTH_OPTIONS = [
  { value: 7, label: "1 week" },
  { value: 14, label: "2 weeks" },
  { value: 21, label: "3 weeks" },
];

export const SPRINT_WEEKDAY_OPTIONS = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

const selectClassName =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

export function teamSprintCadence(team) {
  const length = team?.sprint_length_days ?? team?.sprintLengthDays;
  const weekday = team?.sprint_start_weekday ?? team?.sprintStartWeekday;
  if (length == null || weekday == null || length === "" || weekday === "") {
    return null;
  }
  return {
    lengthDays: Number(length),
    startWeekday: Number(weekday),
  };
}

export function cadenceFormValues(team) {
  const stored = teamSprintCadence(team);
  return {
    sprint_length_days: stored?.lengthDays ?? DEFAULT_SPRINT_LENGTH_DAYS,
    sprint_start_weekday: stored?.startWeekday ?? DEFAULT_SPRINT_START_WEEKDAY,
  };
}

function toDateOnly(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function addDaysDateOnly(dateOnly, days) {
  const [y, m, d] = dateOnly.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  return toDateOnly(dt);
}

function weekdayOfDateOnly(dateOnly) {
  const [y, m, d] = dateOnly.split("-").map(Number);
  return new Date(y, m - 1, d).getDay();
}

function snapToWeekday(dateOnly, weekday) {
  const current = weekdayOfDateOnly(dateOnly);
  const add = (weekday - current + 7) % 7;
  return addDaysDateOnly(dateOnly, add);
}

export function firstSprintStartMin(today, lengthDays) {
  const todayKey = typeof today === "string" ? today.slice(0, 10) : toDateOnly(today);
  const days = Number(lengthDays);
  const span = Number.isFinite(days) && days >= 1 ? Math.floor(days) : 14;
  return addDaysDateOnly(todayKey, -(span - 1));
}

export function firstSprintStartMax(today, startWeekday) {
  const todayKey = typeof today === "string" ? today.slice(0, 10) : toDateOnly(today);
  return snapToWeekday(todayKey, startWeekday);
}

export function allowedFirstSprintStarts(today, cadence) {
  const todayKey = typeof today === "string" ? today.slice(0, 10) : toDateOnly(today);
  const min = firstSprintStartMin(todayKey, cadence.lengthDays);
  const max = firstSprintStartMax(todayKey, cadence.startWeekday);
  const dates = [];
  let cursor = min;
  while (cursor <= max) {
    if (weekdayOfDateOnly(cursor) === cadence.startWeekday) {
      dates.push(cursor);
    }
    cursor = addDaysDateOnly(cursor, 1);
  }
  return dates;
}

export function latestAllowedFirstSprintStart(today, cadence) {
  const allowed = allowedFirstSprintStarts(today, cadence);
  const todayKey = typeof today === "string" ? today.slice(0, 10) : toDateOnly(today);
  return (
    allowed[allowed.length - 1] ??
    firstSprintStartMax(todayKey, cadence.startWeekday)
  );
}

export function isAllowedFirstSprintStart(startsAt, cadence, today = new Date()) {
  if (!startsAt || !cadence) return false;
  return allowedFirstSprintStarts(today, cadence).includes(startsAt.slice(0, 10));
}

function weekdayLabel(weekday) {
  return (
    SPRINT_WEEKDAY_OPTIONS.find((opt) => opt.value === Number(weekday))
      ?.label || "that weekday"
  );
}

function formatRangeBound(dateOnly) {
  try {
    const opts = { month: "short", day: "numeric" };
    return new Date(`${dateOnly}T12:00:00`).toLocaleDateString(undefined, opts);
  } catch {
    return dateOnly;
  }
}

function formatPreview(start, end) {
  try {
    const opts = { weekday: "short", month: "short", day: "numeric" };
    const a = new Date(`${start}T12:00:00`);
    const b = new Date(`${end}T12:00:00`);
    return `Sprint would be ${a.toLocaleDateString(undefined, opts)} – ${b.toLocaleDateString(undefined, opts)}`;
  } catch {
    return `Sprint would be ${start} – ${end}`;
  }
}

function snapStartsAtIfNeeded(startsAt, lengthDays, startWeekday, onStartsAtChange) {
  if (!onStartsAtChange) return;
  const allowed = allowedFirstSprintStarts(new Date(), {
    lengthDays,
    startWeekday,
  });
  if (!startsAt || !allowed.includes(startsAt)) {
    onStartsAtChange(allowed[allowed.length - 1] || startsAt);
  }
}

/**
 * Length (1/2/3 weeks) + start weekday with a live first-sprint preview.
 * Pass startsAt + onStartsAtChange for the Sprint 1 date picker
 * (last cycle through the next start weekday).
 */
export default function SprintCadenceFields({
  lengthDays,
  startWeekday,
  onChange,
  startsAt,
  onStartsAtChange,
  disabled = false,
  idPrefix = "sprint-cadence",
}) {
  const length = Number(lengthDays) || DEFAULT_SPRINT_LENGTH_DAYS;
  const weekday = Number.isInteger(Number(startWeekday))
    ? Number(startWeekday)
    : DEFAULT_SPRINT_START_WEEKDAY;
  const showStartPicker = typeof onStartsAtChange === "function";
  const todayKey = toDateOnly(new Date());
  const minStart = firstSprintStartMin(todayKey, length);
  const maxStart = firstSprintStartMax(todayKey, weekday);
  const startValid =
    !showStartPicker ||
    isAllowedFirstSprintStart(startsAt, {
      lengthDays: length,
      startWeekday: weekday,
    });

  const preview = useMemo(() => {
    const start = showStartPicker && startsAt
      ? startsAt.slice(0, 10)
      : snapToWeekday(todayKey, weekday);
    const end = addDaysDateOnly(start, length - 1);
    return formatPreview(start, end);
  }, [length, weekday, startsAt, showStartPicker, todayKey]);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-length`}>Sprint length</Label>
        <select
          id={`${idPrefix}-length`}
          className={selectClassName}
          value={lengthDays}
          disabled={disabled}
          onChange={(e) => {
            const nextLength = parseInt(e.target.value, 10);
            onChange?.({
              lengthDays: nextLength,
              startWeekday,
            });
            snapStartsAtIfNeeded(
              startsAt,
              nextLength,
              startWeekday,
              onStartsAtChange
            );
          }}
        >
          {SPRINT_LENGTH_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-weekday`}>Start weekday</Label>
        <select
          id={`${idPrefix}-weekday`}
          className={selectClassName}
          value={startWeekday}
          disabled={disabled}
          onChange={(e) => {
            const nextWeekday = parseInt(e.target.value, 10);
            onChange?.({
              lengthDays,
              startWeekday: nextWeekday,
            });
            snapStartsAtIfNeeded(
              startsAt,
              lengthDays,
              nextWeekday,
              onStartsAtChange
            );
          }}
        >
          {SPRINT_WEEKDAY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      {showStartPicker ? (
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-starts-at`}>Sprint 1 starts</Label>
          <Input
            id={`${idPrefix}-starts-at`}
            type="date"
            min={minStart}
            max={maxStart}
            value={startsAt || ""}
            disabled={disabled}
            onChange={(e) => onStartsAtChange(e.target.value)}
          />
          {startValid ? (
            <p className="text-sm text-muted-foreground">{preview}</p>
          ) : (
            <p className="text-sm text-amber-600 dark:text-amber-500">
              Pick a {weekdayLabel(weekday)} between{" "}
              {formatRangeBound(minStart)} and {formatRangeBound(maxStart)}.
            </p>
          )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{preview}</p>
      )}
    </div>
  );
}
